import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormRawValue } from '@r1-compass-library/utilities';
import { Uuid } from '@r1-compass-library/utilities/uuid';
import {
    deprecatedsalesforceUniqueRecommendationFieldTargetNamePlaceholderValue,
    FileMappingRow,
    PlayFileImport,
} from '../public-api';

export class PlayFileImportForm {
    fileName = new FormControl<string | undefined>(undefined, { nonNullable: true, validators: Validators.required });
    salesforceUniqueFieldSourceName = new FormControl<string | undefined>(undefined, {
        nonNullable: true,
        validators: Validators.required,
    });
    customFileMapping = new FormArray<FormGroup<FileMappingRowForm>>(
        [],
        // `required` must be used in conjunction with array minLength,
        //     otherwise at length of `0`, the `minLength` will not be active.
        [Validators.required, Validators.minLength(1)]
    );

    constructor(playFileImport?: PlayFileImport, playObjectName?: string, recommendationObjectName?: string) {
        if (playFileImport) {
            this.fileName.setValue(playFileImport?.fileName);
            this.salesforceUniqueFieldSourceName.setValue(
                String(playFileImport?.salesforceUniqueFieldSourceName).trim()
            );

            const buildCustomFileMappingRowFormData = () => {
                const uniqueMappingRow: FileMappingRowFormData = {
                    id: 'unique-mapping',
                    mappingSource: playFileImport.salesforceUniqueFieldSourceName,
                    recommendationFieldName: deprecatedsalesforceUniqueRecommendationFieldTargetNamePlaceholderValue,
                };
                this.customFileMapping.push(new FormGroup(new FileMappingRowForm(uniqueMappingRow)));

                if (playFileImport.customFileMapping) {
                    const data = createFileMappingRowFormData(
                        playFileImport.customFileMapping,
                        playObjectName,
                        recommendationObjectName
                    );
                    data.forEach(mapping => {
                        this.customFileMapping.push(new FormGroup(new FileMappingRowForm(mapping)));
                    });
                }
            };

            buildCustomFileMappingRowFormData();
        }
    }
}

export function createFormValue(
    playImporterForm: FormGroup<PlayFileImportForm>,
    playFileImport?: PlayFileImport,
    playObjectName?: string,
    recommendationObjectName?: string
): FormRawValue<FormGroup<PlayFileImportForm>> {
    // Create an intermediate helper form with all the passed in data and hold onto its value
    const newFormValue = new FormGroup(
        new PlayFileImportForm(playFileImport, playObjectName, recommendationObjectName)
    ).getRawValue();

    // For each custom file mapping in the intermediate `newFormValue`,
    //     add an empty control to the final form's custom file mapping FormArray that we intend to set the value of.
    //     Once the final form has X default form array controls, then they can safely have their value set using the
    //     helper form's value.
    //
    // Otherwise, we get the following error:
    //     ERROR RuntimeError: NG01000:
    //     There are no form controls registered with this array yet. If you're using ngModel,
    //     you may want to check next tick (e.g. use setTimeout).
    newFormValue.customFileMapping.forEach(mapping => {
        playImporterForm.controls.customFileMapping.push(new FormGroup(new FileMappingRowForm(undefined)));
    });

    return newFormValue;
}

export class FileMappingRowForm {
    mappingId = new FormControl<string>('', { validators: Validators.required, nonNullable: true }); // divide into 2
    source = new FormControl<string>('', { validators: Validators.required, nonNullable: true });

    recommendationFieldName = new FormControl<string>('', {
        validators: Validators.required,
        nonNullable: true,
    });

    constructor(data?: FileMappingRowFormData) {
        this.mappingId.setValue(data?.id ?? Uuid.generate());
        this.source.setValue(data?.mappingSource ?? '');
        if (data?.recommendationFieldName) {
            this.recommendationFieldName.setValue(data.recommendationFieldName);
        }
    }
}

export type FileMappingRowFormData = {
    id: string;
    mappingSource: string;
    playMappingFieldName?: string;
    recommendationFieldName?: string;
};

export function createFileMappingRowFormData(
    fileMappingRows: FileMappingRow[],
    playObjectName?: string,
    recommendationObjectName?: string
): FileMappingRowFormData[] {
    const mappingRowIdMap: Map<string, FileMappingRow[]> = new Map();
    fileMappingRows.forEach(mapping => {
        if (mappingRowIdMap.has(mapping.id)) {
            mappingRowIdMap.get(mapping.id)?.push(mapping);
        } else {
            mappingRowIdMap.set(mapping.id, [mapping]);
        }
    });
    const data: FileMappingRowFormData[] = [];
    mappingRowIdMap.forEach((value, key) => {
        data.push({
            id: key,
            mappingSource: String(value.at(0)?.source).trim(),
            playMappingFieldName: value.find(mapping => mapping.target.objectName === playObjectName)?.target.name,
            recommendationFieldName: value.find(mapping => mapping.target.objectName === recommendationObjectName)
                ?.target.name,
        });
    });
    return data;
}
